/**
 * Global fetch interceptor — JWT Staleness gate.
 *
 * Why a global patch?
 * -------------------
 * The codebase has ~30+ direct `fetch(...)` call-sites scattered across
 * agency CRM/CMS components (CRMFilterBar, ActivityPanel, AIDestination
 * Actions, marketing builder pages, etc). Wrapping every one of them in
 * a `try { ... } catch (tokenStale)` block would (a) be invasive, (b)
 * miss new callers added in the future, and (c) duplicate logic across
 * the codebase.
 *
 * Instead, we install ONE wrapper around `window.fetch` at app boot:
 *
 *   - Forwards every call unchanged (same arguments, same return type).
 *   - On a `401` response carrying the `X-Token-Stale: 1` header (or a
 *     `detail/code === "TOKEN_STALE"` body), trigger the auth-reset +
 *     redirect side-effect ONCE.
 *
 * The Response object is delivered to the caller untouched. We use
 * `response.clone()` to peek at the body without disturbing the
 * original stream — so existing call-site logic (`r.json()` /
 * `r.text()`) keeps working.
 *
 * This module is dead-simple, has zero React/router deps, and is safe
 * to import from `index.js` before the React tree renders.
 */

import {
  getAuthReason,
  handleAuthInvalidation,
} from './tokenStale.js';

let _installed = false;

export const installFetchInterceptor = () => {
  if (_installed) return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  _installed = true;

  const original = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await original(...args);

    // Fast path — only inspect 401 responses, skip everything else.
    if (response && response.status === 401) {
      // Cheap header check first — avoids body clone in 99% of cases.
      let reason = null;
      try {
        reason = getAuthReason(response, null);
      } catch { /* unreadable headers — fall back to body parse */ }

      if (!reason) {
        // Body fallback. `clone()` so the caller's `r.json()` still works.
        try {
          const body = await response.clone().json().catch(() => null);
          reason = getAuthReason(response, body);
        } catch { /* ignore */ }
      }

      if (reason) {
        handleAuthInvalidation(reason);
      }
    }

    return response;
  };
};

// Test-only escape hatch.
export const _uninstallFetchInterceptorForTest = (originalFetch) => {
  if (originalFetch) {
    window.fetch = originalFetch;
  }
  _installed = false;
};

export default installFetchInterceptor;
